import {
    Box,
    Button,
    Collapse,
    IconButton,
    LinearProgress,
    TextField,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Delete, Edit } from "@mui/icons-material";
import React, { useState } from "react";
import { ILeadQuelle } from "../../interfaces/ILeadQuelle";
import { CustomSnackbar } from "../../components/core/Snackbar";
import { IFeedback } from "../../interfaces/IFeedback";
import { useGetLeadQuellen, useSaveLeadQuelle, useDeleteLeadQuelle } from "../../hooks/data/data_leadquelle";

export const LeadQuellenOverview: React.FC = () => {
    const { leadQuellen, isLoadingLeadQuellen } = useGetLeadQuellen();
    const saveLeadQuelle = useSaveLeadQuelle();
    const deleteLeadQuelle = useDeleteLeadQuelle();

    const [searchTerm, setSearchTerm] = useState("");
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<ILeadQuelle | null>(null);
    const [feedback, setFeedback] = useState<IFeedback>({
        open: false,
        message: "",
        severity: "info",
    });

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState<number | null>(null);

    const filteredData = leadQuellen?.filter((item) =>
        item.leadQuelle.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const handleEdit = (item: ILeadQuelle) => {
        setCurrentItem(item);
        setEditDialogOpen(true);
    };

    const handleDelete = (id: number) => {
        setDeleteId(id);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (deleteId !== null) {
            deleteLeadQuelle.mutate(deleteId, {
                onSuccess: () => {
                    setFeedback({
                        open: true,
                        message: "Leadquelle gelöscht!",
                        severity: "success",
                    });
                    setDeleteDialogOpen(false);
                },
                onError: () => {
                    setFeedback({
                        open: true,
                        message: "Fehler beim Löschen.",
                        severity: "error",
                    });
                },
            });
        }
    };

    const handleSave = () => {
        if (!currentItem) return;

        saveLeadQuelle.mutate(currentItem, {
            onSuccess: () => {
                setFeedback({
                    open: true,
                    message: currentItem.id === 0 ? "Leadquelle erstellt!" : "Leadquelle bearbeitet!",
                    severity: "success",
                });
                setEditDialogOpen(false);
                setCurrentItem(null);
            },
            onError: () => {
                setFeedback({
                    open: true,
                    message: "Fehler beim Speichern.",
                    severity: "error",
                });
            },
        });
    };

    const handleNew = () => {
        setCurrentItem({ id: 0, leadQuelle: "" });
        setEditDialogOpen(true);
    };

    const columns: GridColDef[] = [
        { field: "id", headerName: "Nr.", width: 100 },
        { field: "leadQuelle", headerName: "Leadquelle", flex: 1 },
        {
            field: "actions",
            headerName: "Aktionen",
            width: 100,
            renderCell: (params: GridRenderCellParams) => (
                <>
                    <IconButton onClick={() => handleEdit(params.row)}>
                        <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(params.row.id)}>
                        <Delete />
                    </IconButton>
                </>
            ),
        },
    ];

    if (isLoadingLeadQuellen) {
        return <LinearProgress />;
    }

    return (
        <>
            <Typography variant="h5">
                Leadquellen
                <Button sx={{ float: "right" }} variant="outlined" onClick={handleNew}>
                    Leadquelle hinzufügen
                </Button>
            </Typography>

            <Collapse in={saveLeadQuelle.isPending || deleteLeadQuelle.isPending}>
                <LinearProgress />
            </Collapse>

            <CustomSnackbar feedback={feedback} setFeedback={setFeedback} />

            <TextField
                sx={{ my: 2 }}
                label="Suche"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />

            <Box sx={{ height: "70vh" }}>
                <DataGrid rows={filteredData || []} columns={columns} getRowId={(row) => row.id} autoHeight />
            </Box>

            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
                <DialogTitle>{currentItem?.id === 0 ? "Neue Leadquelle" : "Leadquelle bearbeiten"}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Leadquelle"
                        fullWidth
                        margin="dense"
                        value={currentItem?.leadQuelle || ""}
                        onChange={(e) =>
                            setCurrentItem((prev) => (prev ? { ...prev, leadQuelle: e.target.value } : null))
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Abbrechen</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saveLeadQuelle.isPending}>
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Löschen bestätigen</DialogTitle>
                <DialogContent>Möchten Sie diese Leadquelle wirklich löschen?</DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
                    <Button onClick={confirmDelete} variant="contained" color="error">
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
